import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Row, Col, Badge, Alert, Spinner, Form, Button, Pagination } from 'react-bootstrap';
import { RiMapPin2Fill, RiStackFill, RiBuildingFill, RiGroupFill, RiFileList3Fill, RiCheckboxCircleFill, RiPhoneFill, RiForbidLine, RiBarChartFill, RiSearchLine, RiListCheck, RiArrowRightSLine, RiArrowLeftLine, RiHome4Fill, RiTimeLine, RiUploadCloud2Line } from 'react-icons/ri';
import api from '../../service/api.js';
import { useToast } from '../../contexts/ToastContext';

const UserDashboard = () => {
  const { user, token: authToken } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const queueRequest = useRef(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHistoryCount, setTotalHistoryCount] = useState(0);
  const HISTORY_FILTERS = ['completed', 'unreachable', 'call_back'];
  const HISTORY_PAGE_SIZE = 10;
  const isHistoryFilter = HISTORY_FILTERS.includes(activeFilter);
  const [questionsError, setQuestionsError] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [submittingAnswers, setSubmittingAnswers] = useState(false);
  const [touched, setTouched] = useState({});


  const statuses = [
    { key: 'pending', label: 'Pending', Icon: RiFileList3Fill },
    { key: 'completed', label: 'Completed', Icon: RiCheckboxCircleFill },
    { key: 'call_back', label: 'Call Back', Icon: RiPhoneFill },
    { key: 'unreachable', label: 'Unreachable', Icon: RiForbidLine }
  ];
  const fetchSummary = async () => {
    try {
      const response = await api.get('/api/targets/top', { params: { summary: true } });
      const data = response.data;
      if (!data || Array.isArray(data) || !data.area || !data.counts ||
          !Number.isFinite(data.area.total) || !Number.isFinite(data.area.completed) ||
          !['pending', 'completed', 'call_back', 'unreachable'].every(key => Number.isFinite(data.counts[key]))) {
        throw new Error('Dashboard summary is unavailable from the running API.');
      }
      setSummary(response.data);
      setSummaryError(false);
    } catch {
      setSummary(null);
      setSummaryError(true);
      showWarning('Dashboard totals are unavailable. The queue can still be used. Refresh Queue to retry; if this continues, the dashboard API needs to be updated or restarted.');
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setCurrentPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    if (authToken && user) fetchSummary();
  }, [authToken, user]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoadingQuestions(true);
        const response = await api.get('/api/questions', {
          params: { userId: user._id || user.id },
        });
        setQuestions(response.data);
        setQuestionsError(false);

      } catch (err) {
        setQuestionsError(true);
        showError('Failed to load questions assigned to you.');
      } finally {
        setLoadingQuestions(false);
      }
    };

    if (authToken && user) {
      fetchQuestions();
      fetchTargets(activeFilter, HISTORY_FILTERS.includes(activeFilter) ? currentPage : 1);
    } else {
      console.log('Dashboard: not fetching, authToken:', !!authToken, 'user:', !!user);
    }
  }, [authToken, user, activeFilter, currentPage, search]);

  const handleSelectTarget = (target) => {
    setSelectedTarget(target);
    setAnswers({});
  };

  const fetchTargets = async (status = activeFilter, page = currentPage) => {
    const request = ++queueRequest.current;
    const historyMode = HISTORY_FILTERS.includes(status);
    try {
      setLoadingTargets(true);
      console.log('[Dashboard] fetchTargets:start', {
        status,
        page: historyMode ? page : 1,
        activeFilter,
        userId: user?._id || user?.id,
        username: user?.username
      });
      const params = { status, ...(search ? { search } : {}) };
      if (historyMode) {
        params.page = page;
        params.limit = HISTORY_PAGE_SIZE;
      }
      const response = await api.get('/api/targets/top', { params });
      if (request !== queueRequest.current) return;

      if (historyMode) {
        const d = response.data || {};
        const items = Array.isArray(d.items) ? d.items : [];
        const total = typeof d.total === 'number' ? d.total : 0;
        const tp = typeof d.totalPages === 'number' ? d.totalPages : Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE) || 1);
        console.log('[Dashboard] fetchTargets:response (history)', {
          status,
          page: d.page,
          count: items.length,
          total,
          totalPages: tp
        });
        setTargets(items);
        setTotalHistoryCount(total);
        setTotalPages(tp);
        if (typeof d.page === 'number' && d.page !== page) {
          setCurrentPage(d.page);
        }
      } else {
        console.log('[Dashboard] fetchTargets:response', {
          status,
          isArray: Array.isArray(response.data),
          count: Array.isArray(response.data) ? response.data.length : 0,
          sample: Array.isArray(response.data) && response.data.length > 0
            ? {
                id: response.data[0]._id,
                callStatus: response.data[0].callStatus,
                assignedTo: response.data[0].assignedTo
              }
            : null
        });
        if (Array.isArray(response.data)) {
          setTargets(response.data);
        } else {
          console.warn('[Dashboard] fetchTargets:unexpected payload format', response.data);
          setTargets([]);
        }
        setTotalPages(1);
        setTotalHistoryCount(0);
        setCurrentPage(1);
      }
    } catch (err) {
      if (request !== queueRequest.current) return;
      showError('Could not load the queue. Please try again.');
      setTargets([]);
      setTotalHistoryCount(0);
      setTotalPages(1);
    } finally {
      if (request === queueRequest.current) setLoadingTargets(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    setCurrentPage(1);
    setSelectedTarget(null);
    setTargets([]);
  };

  const handleNext = () => {
    fetchSummary();
    const remaining = targets.filter(t => t._id !== selectedTarget._id);

    if (remaining.length > 0) {
      setTargets(remaining);
      setSelectedTarget(remaining[0]);
    } else {
      setSelectedTarget(null);
      fetchTargets(activeFilter, HISTORY_FILTERS.includes(activeFilter) ? currentPage : 1);
    }

    setAnswers({});
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxAnswerChange = (questionId, option) => {
    setAnswers(prev => {
      const currentValue = prev[questionId] || [];
      const isSelected = Array.isArray(currentValue) && currentValue.includes(option);
      const updatedValue = isSelected
        ? currentValue.filter(item => item !== option)
        : [...currentValue, option];
      return {
        ...prev,
        [questionId]: updatedValue
      };
    });
  };

  const showSuccessAndRefreshQueue = (message) => {
    showSuccess(message);
    handleNext();
  };

  const handleSubmitAnswers = async () => {
    setSubmittingAnswers(true);
    const allTouched = {};
    questions.forEach(q => {
      allTouched[q._id] = true;
    });
    setTouched(allTouched);

    try {
      const missingRequired = questions.filter(
        q =>
          q.isRequired &&
          (!answers[q._id] || String(answers[q._id]).trim() === '')
      );

      if (missingRequired.length > 0) {

        const fieldNames = missingRequired.map(q => q.questionText);
        showError(`Please fill required fields: ${fieldNames.join(', ')}`);

        const firstMissingId = missingRequired[0]._id;
        const element = document.getElementById(`q-${firstMissingId}`);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('shake-animation');
          setTimeout(() => element.classList.remove('shake-animation'), 500);
        }
        setSubmittingAnswers(false);
        return;
      }
      const response = await api.post('/api/answers/bulk', {
        voterId: selectedTarget._id,
        answers: answers
      });
      showSuccess(response.data.message || 'Survey saved successfully!');
      handleNext();
    } catch (err) {
      const serverMsg =
        err.response?.data?.message ||
        'Failed to save survey. Connection issue?';

      showError(serverMsg);

    } finally {
      setSubmittingAnswers(false);
    }
  };

  const handleAction = async (endpoint) => {
    setSubmittingAnswers(true);
    try {
      const response = await api.post(`/api/targets/${selectedTarget._id}/${endpoint}`);
      const fallbackMessage = endpoint === 'callback'
        ? 'Call back status saved.'
        : 'Unreachable status saved.';
      showSuccessAndRefreshQueue(response?.data?.message || fallbackMessage);
    } catch (err) {
      showError(err.response?.data?.message || `Failed to mark target as ${endpoint}`);
    } finally {
      setSubmittingAnswers(false);
    }
  };

  const isReadOnlyTarget = selectedTarget
    ? ['completed', 'unreachable'].includes(selectedTarget.callStatus)
    : false;

  const area = summary?.area;
  const progress = area?.total ? Math.round(area.completed / area.total * 100) : 0;
  const showValues = (values) => values?.length ? values.join(', ') : '—';
  const areaDetails = [
    ['Assigned Level', area?.level || (user?.assignedBooth ? 'Booth' : user?.assignedWardNo ? 'Ward' : user?.assignedWard ? 'Assembly' : '—'), RiStackFill],
    ['Ward No.', showValues(area?.wards?.length ? area.wards : user?.assignedWardNo ? [user.assignedWardNo] : []), RiMapPin2Fill],
    ['Assembly No.', showValues(area?.assemblies?.length ? area.assemblies : user?.assignedWard ? [user.assignedWard] : []), RiBuildingFill],
    ['Assembly Name', showValues(area?.assemblyNames), RiBuildingFill],
    ['Distinct Booths', area?.distinctBooths ?? '—', RiGroupFill],
    ['Total Assigned Voters', area?.total ?? '—', RiGroupFill]
  ];

  return (
    <div className={`telecaller-dashboard${selectedTarget ? ' td-detail-page' : ''}`}>
      <div className="td-content">
        <section className="td-panel td-area" aria-labelledby="td-area-title">
          <div className="td-section-heading">
            <span className="td-icon"><RiMapPin2Fill aria-hidden="true" /></span>
            <div className="td-heading-copy">
              <h1 id="td-area-title">Assigned Area Details</h1>
              <p>Primary call area for @{user?.username}</p>
            </div>
            <div className="td-session"><span /> Session Active</div>
          </div>
          <div className="td-area-grid">
            {areaDetails.map(([label, value, Icon]) => (
              <div className="td-area-detail" key={label}>
                <span>{label}</span><strong><Icon aria-hidden="true" />{value}</strong>
              </div>
            ))}
          </div>
        </section>
        {!selectedTarget ? (
          <>
            <div className="td-status-grid">
              {statuses.map(({ key, label, Icon }) => (
                <button type="button" key={key} className={`td-panel td-status td-${key}`} onClick={() => handleFilterChange(key)} aria-label={`Show ${label} queue`}>
                  <span className="td-icon"><Icon aria-hidden="true" /></span>
                  <span className="td-status-copy"><span>{label}</span><strong>{summary?.counts?.[key] ?? '—'}</strong><small>In your queue</small></span>
                  <span className="td-status-arrow"><RiArrowRightSLine aria-hidden="true" /></span>
                </button>
              ))}
            </div>
            <div className="td-overview-grid">
            <section className="td-panel td-progress" aria-labelledby="td-progress-title">
              <span className="td-icon"><RiBarChartFill aria-hidden="true" /></span>
              <div className="td-progress-content">
                <div className="td-progress-heading">
                  <div><h2 id="td-progress-title">Survey Progress</h2><p>{loadingQuestions ? 'Loading questions...' : questionsError ? 'Questions unavailable' : `${questions.length} assigned questions`} | Assigned area completion</p></div>
                  <div className="td-progress-number"><strong>{area ? `${progress}%` : '—'}</strong><p>{area ? `${area.completed} of ${area.total} completed` : 'Totals unavailable'}</p></div>
                </div>
                <div className="td-progress-track" role="progressbar" aria-label="Assigned area survey completion" aria-valuemin={0} aria-valuemax={100} aria-valuenow={area ? progress : undefined}>
                  <span style={{ width: `${progress}%` }} />
                </div>
              </div>
            </section>
            <section className="td-panel td-snapshot" aria-labelledby="td-snapshot-title">
              <div className="td-snapshot-heading">
                <RiBarChartFill aria-hidden="true" />
                <div><h2 id="td-snapshot-title">Last Week Snapshot</h2><p>Your activity comparison</p></div>
              </div>
              <div className="td-snapshot-values">
                {[
                  ['callsMade', 'Calls Made'], ['completed', 'Completed'],
                  ['callback', 'Callback'], ['unreachable', 'Unreachable']
                ].map(([key, label]) => (
                  <div key={key}><strong>{summary?.lastWeek?.[key] ?? 0}</strong><span>{label}</span></div>
                ))}
              </div>
              {!summary?.lastWeek && <p className="td-snapshot-note">Historical activity is not available yet.</p>}
            </section>
            </div>
            <section className="td-panel td-queue" aria-labelledby="td-queue-title">
              <div className="td-queue-header">
                <div className="td-section-heading">
                  <span className="td-icon"><RiListCheck aria-hidden="true" /></span>
                  <div><h2 id="td-queue-title">My Action Queue</h2><p>Voters assigned to you. Make calls and complete the survey.</p></div>
                </div>
                <div className="td-queue-controls">
                  <label className="td-search"><RiSearchLine aria-hidden="true" /><input type="search" aria-label="Search voters by name, EPIC or phone" placeholder="Search by name, EPIC or phone..." value={searchInput} onChange={event => setSearchInput(event.target.value)} /></label>
                  <Button size="sm" variant="outline-primary" disabled={loadingTargets} onClick={() => { fetchTargets(activeFilter, isHistoryFilter ? currentPage : 1); fetchSummary(); }}>Refresh Queue</Button>
                </div>
              </div>
              <div className="td-tabs" aria-label="Queue status filters">
                {statuses.map(({ key, label }) => <button type="button" key={key} className={activeFilter === key ? 'active' : ''} aria-pressed={activeFilter === key} onClick={() => handleFilterChange(key)}>{label}{summary?.counts?.[key] != null ? ` (${summary.counts[key]})` : ''}</button>)}
              </div>
              {loadingTargets ? <div className="td-empty" role="status"><Spinner animation="border" variant="primary" /><p>Loading your queue...</p></div>
                : targets.length === 0 ? <div className="td-empty"><RiCheckboxCircleFill aria-hidden="true" /><h3>{search ? 'No matching voters' : 'No voters in this queue'}</h3><p>{search ? 'Try a different name, EPIC number or phone number.' : 'Refresh to check for available assignments.'}</p></div>
                : <div className="td-table-wrap" tabIndex={0} role="region" aria-label="Voter queue table">
                  <table className="td-table">
                    <thead><tr>{['#', 'Voter Name', 'EPIC Number', 'Area / Ward', 'Phone Number', 'Status'].map(label => <th scope="col" key={label}>{label}</th>)}</tr></thead>
                    <tbody>{targets.map((target, index) => <tr key={target._id} className="td-voter-row" onClick={() => handleSelectTarget(target)}>
                      <td>{(isHistoryFilter ? (currentPage - 1) * HISTORY_PAGE_SIZE : 0) + index + 1}</td>
                      <td><div className="td-voter"><span className="td-avatar">{target.firstName?.[0]}{target.lastName?.[0]}</span><button type="button" className="td-voter-link" onClick={event => { event.stopPropagation(); handleSelectTarget(target); }} aria-label={`Open record for ${target.firstName} ${target.lastName || ''}`}>{target.firstName} {target.lastName}</button></div></td>
                      <td className="td-epic">{target.epicNo || '—'}</td>
                      <td>{[target.acName, target.ward ? `Ward ${target.ward}` : null].filter(Boolean).join(' / ') || '—'}</td>
                      <td><span className="td-phone"><RiPhoneFill aria-hidden="true" />{target.phone || 'No mobile'}</span></td>
                      <td><span className={`td-status-badge td-${target.callStatus}`}>{statuses.find(item => item.key === target.callStatus)?.label || target.callStatus}</span></td>
                    </tr>)}</tbody>
                  </table>
                </div>}
              <div className="td-queue-footer">
                <small>{isHistoryFilter ? `${totalHistoryCount} results | Page ${currentPage} of ${totalPages}` : `${targets.length} voters in this batch | Up to 10 at a time`}</small>
                {isHistoryFilter && totalHistoryCount > 0 && <Pagination className="mb-0"><Pagination.Prev disabled={currentPage <= 1 || loadingTargets} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} /><Pagination.Next disabled={currentPage >= totalPages || loadingTargets} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} /></Pagination>}
              </div>
            </section>
          </>
        ) : (
          <div className="td-interaction">
            <section className="td-panel td-person-card" aria-label="Voter summary">
              <div className="td-person-toolbar">
                <Button className="td-return" variant="outline-primary" onClick={() => {
                  setSelectedTarget(null); setAnswers({});
                }}><RiArrowLeftLine aria-hidden="true" />Return to Queue</Button>
                <div className="td-call-state"><span><RiPhoneFill aria-hidden="true" /> CALL IN PROGRESS</span></div>
              </div>
              <div className="td-person-header">
                <div className="td-person-identity">
                  <div className="td-person-avatar">{selectedTarget.firstName?.[0] || ''}{selectedTarget.lastName?.[0] || ''}</div>
                  <div className="td-person-copy">
                    <h2>{selectedTarget.firstName} {selectedTarget.lastName}</h2>
                    <p className="td-person-epic">EPIC: <span>{selectedTarget.epicNo || 'N/A'}</span></p>
                  </div>
                </div>
                <div className="td-person-phone" aria-label="Primary contact number"><span className="td-phone-icon"><RiPhoneFill aria-hidden="true" /></span><strong>{selectedTarget.phone || 'DATA MISSING'}</strong></div>
              </div>
              <div className="td-quick-info">
                {[
                  ['Assembly No.', selectedTarget.acNo, RiBuildingFill],
                  ['Ward No.', selectedTarget.ward, RiMapPin2Fill],
                  ['Booth No.', selectedTarget.booth, RiGroupFill],
                  ['Res. No.', selectedTarget.resNo, RiHome4Fill]
                ].map(([label, value, Icon]) => <div className="td-quick-item" key={label}><span className="td-icon"><Icon aria-hidden="true" /></span><div><span>{label}</span><strong>{value || 'N/A'}</strong></div></div>)}
              </div>
            </section>
            <section className="td-panel td-detail-groups" aria-label="Voter details">
              {[
                { title: 'Voter Information', Icon: RiFileList3Fill, fields: [
                  ['Voters First Name', selectedTarget.firstName], ['Voters Surname', selectedTarget.lastName],
                  ['Election Card No.', selectedTarget.epicNo], ['House No.', selectedTarget.houseNo],
                  ['Eng. House No.', selectedTarget.engHouseNo], ['Res. No.', selectedTarget.resNo]
                ] },
                { title: 'Constituency Details', Icon: RiBuildingFill, fields: [
                  ['Assembly No.', selectedTarget.acNo], ['Assembly Name', selectedTarget.acName],
                  ['Ward No.', selectedTarget.ward], ['Booth No.', selectedTarget.booth]
                ] },
                { title: 'Location Details', Icon: RiMapPin2Fill, fields: [
                  ['Polling Station Name', selectedTarget.pollingStationName || selectedTarget.engMathak],
                  ['Polling Station Address', selectedTarget.pollingStationAddress], ['Society Name', selectedTarget.societyName],
                  ['Address', selectedTarget.address], ['English Address', selectedTarget.engAddress]
                ] }
              ].map(({ title, Icon, fields }) => <section className="td-detail-group" key={title}>
                <div className="td-group-heading"><span className="td-icon"><Icon aria-hidden="true" /></span><h2>{title}</h2></div>
                <dl>{fields.map(([label, value]) => <div className="td-detail-field" key={label}><dt>{label}</dt><dd>{value || 'N/A'}</dd></div>)}</dl>
              </section>)}
            </section>
            <Card className="td-panel td-survey-card">
              <Card.Header className="td-survey-header">
                <span className="td-icon"><RiFileList3Fill aria-hidden="true" /></span>
                <div><h2>Interaction Survey</h2><p>Ask the questions and record the voter response</p></div>
              </Card.Header>
              <Card.Body className="td-survey-body">
                {isReadOnlyTarget && (
                  <Alert variant="info" className="rounded-3 border-0">
                    This record is read-only because it is already marked as {selectedTarget.callStatus}.
                  </Alert>
                )}
                {loadingQuestions ? (
                  <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                ) : questions.length === 0 ? (
                  <Alert variant="warning" className="rounded-3 border-0">No questions assigned by the administration.</Alert>
                ) : (
                  <Form className="td-survey-form">
                    <Row className="g-4">
                      {questions.map((question) => {
                        const value = answers[question._id] || '';
                        const isMissing =
                          question.isRequired &&
                          touched[question._id] &&
                          (!value || String(value).trim() === '');

                        return (
                          <Col xs={12} md={6} key={question._id} id={`q-${question._id}`}>
                            <Form.Group
                              className={`td-question ${isMissing
                                ? ' shadow-sm'
                                : 'bg-slate-50'
                                }`}
                            >

                              <div className="td-question-caption">{questions.length === 1 ? 'Current Question' : 'Survey Question'}</div>
                              <Form.Label className="fw-bold fs-6 mb-2 d-flex align-items-center gap-2 flex-wrap">
                                {question.questionText}

                                {question.isRequired && (
                                  <Badge
                                    bg={isMissing ? 'danger' : 'secondary'}
                                    className="bg-opacity-10 text-uppercase"
                                    style={{
                                      color: isMissing ? '#dc3545' : '#64748b',
                                      fontSize: '0.6rem'
                                    }}
                                  >
                                    {isMissing ? 'Required Field' : 'Required'}
                                  </Badge>
                                )}
                              </Form.Label>
                              {question.questionType === 'radio' ||
                                question.questionType === 'checkbox' ? (
                                <Form.Select
                                  value={value}
                                  onChange={(e) =>
                                    handleAnswerChange(question._id, e.target.value)
                                  }
                                  onBlur={() =>
                                    setTouched((prev) => ({
                                      ...prev,
                                      [question._id]: true
                                    }))
                                  }
                                  isInvalid={isMissing}
                                  className="py-2.5 rounded-3 shadow-none"
                                >
                                  <option value="">Select an option...</option>
                                  {question.options?.map((opt, i) => (
                                    <option key={i} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </Form.Select>
                              ) : question.questionType === 'checkbox' ? (
                                <details className="" style={{ padding: '0.4rem 0.65rem' }}>
                                  <summary
                                    className="d-flex justify-content-between align-items-center text-start"
                                    style={{
                                      listStyle: 'none',
                                      cursor: 'pointer',
                                      padding: '0.65rem 0.5rem',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '0.75rem',
                                      minHeight: '44px',
                                      marginBottom: '0.5rem',
                                      backgroundColor: '#f8fafc'
                                    }}
                                  >
                                    <span style={{ color: answers[question._id]?.length ? '#111827' : '#6b7280' }}>
                                      {Array.isArray(answers[question._id]) && answers[question._id].length > 0
                                        ? answers[question._id].join(', ')
                                        : 'Select one or more options...'}
                                    </span>
                                    <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>▼</span>
                                  </summary>
                                  <div className="d-flex flex-column gap-2 px-2 pb-2">
                                    {question.options?.map((opt, i) => (
                                      <Form.Check
                                        key={i}
                                        type="checkbox"
                                        id={`${question._id}-${i}`}
                                        label={opt}
                                        disabled={isReadOnlyTarget}
                                        checked={Array.isArray(answers[question._id]) && answers[question._id].includes(opt)}
                                        onChange={() => handleCheckboxAnswerChange(question._id, opt)}
                                        className="py-1"
                                      />
                                    ))}
                                  </div>
                                </details>
                              ) : (
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  placeholder="Provide detailed information..."
                                  value={value}
                                  onChange={(e) =>
                                    handleAnswerChange(question._id, e.target.value)
                                  }
                                  onBlur={() =>
                                    setTouched((prev) => ({
                                      ...prev,
                                      [question._id]: true
                                    }))
                                  }
                                  isInvalid={isMissing}
                                  className="py-2.5 rounded-3 shadow-none"
                                />
                              )}
                              <Form.Control.Feedback type="invalid">
                                This field is required
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        );
                      })}
                    </Row>
                    <div className="td-survey-actions">
                      <div className="td-outcome-actions">
                        <Button
                          variant="warning"
                          onClick={() => handleAction('callback')}
                          disabled={submittingAnswers || isReadOnlyTarget}
                          className="rounded-pill px-4 py-2.5 fw-bold text-white shadow-sm border-0 d-flex align-items-center gap-2"
                          style={{ backgroundColor: '#f59e0b' }}
                        >
                          <RiTimeLine aria-hidden="true" />
                          Call Back
                        </Button>

                        <Button
                          variant="danger"
                          onClick={() => handleAction('unreachable')}
                          disabled={submittingAnswers || isReadOnlyTarget}
                          className="rounded-pill px-4 py-2.5 fw-bold shadow-sm border-0 d-flex align-items-center gap-2"
                          style={{ backgroundColor: '#ef4444' }}
                        >
                          <RiForbidLine aria-hidden="true" />
                          Unreachable
                        </Button>
                      </div>

                      <Button
                        variant="primary"
                        onClick={handleSubmitAnswers}
                        disabled={submittingAnswers}
                        className="rounded-pill px-5 py-2.5 fw-bold shadow-lg d-flex align-items-center gap-2"
                      >
                        {submittingAnswers ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <RiUploadCloud2Line aria-hidden="true" />
                            Save Survey & Next
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
